import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { pt, type Dictionary } from './pt'
import { en } from './en'

export type Locale = 'pt' | 'en'

const DICTIONARIES: Record<Locale, Dictionary> = { pt, en }

interface I18nValue {
  locale: Locale
  t: Dictionary
  setLocale: (locale: Locale) => void
  toggleLocale: () => void
}

const I18nContext = createContext<I18nValue | null>(null)

/**
 * O idioma não é persistido — o app não grava nada. A cada carregamento
 * partimos do idioma do navegador; a troca vale só para a sessão.
 */
function detectLocale(): Locale {
  if (typeof navigator === 'undefined') return 'pt'
  const languages = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const language of languages) {
    if (!language) continue
    if (language.toLowerCase().startsWith('pt')) return 'pt'
    if (language.toLowerCase().startsWith('en')) return 'en'
  }
  return 'pt'
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocale] = useState<Locale>(detectLocale)
  const t = DICTIONARIES[locale]

  // Leitores de tela e a hifenização do navegador dependem do lang correto.
  useEffect(() => {
    document.documentElement.lang = t.htmlLang
  }, [t.htmlLang])

  const toggleLocale = useCallback(() => {
    setLocale((current) => (current === 'pt' ? 'en' : 'pt'))
  }, [])

  const value = useMemo<I18nValue>(
    () => ({ locale, t, setLocale, toggleLocale }),
    [locale, t, toggleLocale],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useI18n precisa estar dentro de <I18nProvider>')
  return value
}
